import type {
  ContinuousScrollerSort,
  DataGridCellRendererProps,
  DataGridRef,
  DataGridSettings,
  DynamicFieldComponentProps,
  JsonData,
  JsonDataItem,
  RowTemplateType,
} from '@myosh/odin-components';
import {
  Button,
  DataGrid,
  DataGridColumnType,
  EmptyFieldLabel,
  ErrorLabel,
  FieldLabel,
  RequiredIndicator,
  useScreenSize,
} from '@myosh/odin-components';
import clsx from 'clsx';
import type { Ref } from 'react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Observable, skipWhile, Subject, take, takeUntil } from 'rxjs';
import type { ExtendedDynamicFormWorkflowStep } from '../../../@types/forms';
import type { LinkedRecordResult, RecordLinkProps } from '../../../@types/linked-records-field';
import type { RecordResult } from '../../../@types/records';
import useActiveRecordRef from '../../../hooks/use-active-record-ref';
import useFetchLinkedRecords from '../../../hooks/use-fetch-linked-records';
import useFormPermissions from '../../../hooks/use-form-permissions';
import useFetchWorkflowSteps from '../../../hooks/use-workflow-steps';
import { useModulesQuery } from '../../../redux/services/module';
import { useLazyGetRecordByIdQuery, useSaveLinkedRecordsLayoutMutation } from '../../../redux/services/record';
import { promiseToast } from '../../../services/notification.service';
import { extractLinkedRecordsPermissions } from '../../../utils/form-permissions-util';
import { forceAssert, isEmptyObject } from '../../../utils/type-guards';
import HistoryFieldDataCell from '../../data-grid-data-cells/history-field-data-cell';
import {
  customLinkedRecordsComponents,
  defaultLinkedRecordGridSetting,
} from '../../dynamic-page/default-grid-settings';
import { OdinDataGridRowTemplate } from '../../dynamic-page/odin-data-grid-row-template.component';
import CreateLinkedRecord from './components/create-linked-record';
import LinkedRecordsActionsCell from './components/linked-records-action-cell';
import LinkedRecordsSelecting from './components/linked-records-selecting';
import {
  getColumnSortPath,
  linkedRecordColumnValuePaths,
  linkedRecordsCellRenderer,
  linkedRecordsSortFunction,
  transformLinkedRecordDataWithKeyTypeId,
  transformRecordLinkColumns,
} from './linked-records-functions';
import MobileLinkedBaseRecords from './linked-records-mobile-base.component';

export interface LinkedRecordsProps
  extends DynamicFieldComponentProps<Array<LinkedRecordResult>, Array<string>>,
    RecordLinkProps {
  isPreview?: boolean;
  recordId?: number;
  isTextualQuestionnaireLinkedRecords?: boolean;
  saveRecord?: () => Observable<RecordResult>;
}

export default function LinkedRecords({
  id,
  label,
  value,
  recordLinkFormat,
  targetModuleFormId,
  buttonCaption,
  autoPopulateEnabled = false,
  showSelectButton = false,
  readOnly,
  required,
  onChange,
  isPreview,
  recordId,
  saveRecord,
  error,
  recordLinkRecordCreationDisabled,
  sourceModuleFormId,
  recordLinkType,
  isTextualQuestionnaireLinkedRecords = false,
}: LinkedRecordsProps) {
  const [linkedRecords, setLinkedRecords] = useState<Array<LinkedRecordResult>>([]);
  const [selectLinkedRecordsDialogVisible, setSelectLinkedRecordsDialogVisible] = useState<boolean>(false);
  const [createLinkedRecordDialogState, setCreateLinkedRecordDialogState] = useState<{
    visible: boolean;
    data: RecordResult | undefined;
  }>({ visible: false, data: undefined });
  const [listOfPermittedRecordIds, setListOfPermittedRecordIds] = useState<string[]>([]); //list of record ids you have permissions to access
  const [pendingPermissions, setPendingPermissions] = useState<Array<string>>([]);

  const destroySubject = useRef(new Subject<void>());
  const linkedRecordIdsRef = useRef<string[] | undefined>(undefined);
  const gridRef = useRef<DataGridRef | undefined>(undefined);

  const { activeRecordReference } = useActiveRecordRef();
  const { data: modules, isLoading: areModulesLoading } = useModulesQuery({});
  const [saveLayout, { isLoading }] = useSaveLinkedRecordsLayoutMutation();
  const { formPermissions } = useFormPermissions(targetModuleFormId);
  const { t } = useTranslation();
  const { workflowSteps: linkedWorkflowSteps } = useFetchWorkflowSteps(targetModuleFormId!);

  const [getRecordById] = useLazyGetRecordByIdQuery();

  const { linkedRecords: linkedRecordsData, isFetching: isLinkedRecordsFetching } = useFetchLinkedRecords(
    recordId,
    Number(id),
    linkedRecords.map((record) => String(record.id))
  );

  useEffect(() => {
    return () => {
      destroySubject.current.next();
      destroySubject.current.complete();
    };
  }, []);

  useEffect(() => {
    setPendingPermissions(linkedRecords.map((record) => String(record.id)));
  }, [linkedRecords]);

  const linkedRecordsPermissions = useMemo(() => {
    return extractLinkedRecordsPermissions(formPermissions);
  }, [formPermissions]);

  const canCreateOrReadRecord = linkedRecordsPermissions?.create || linkedRecordsPermissions?.read;
  const canOnlySeeDataGrid = !linkedRecordsPermissions?.create && !linkedRecordsPermissions?.read;

  useEffect(() => {
    if (canCreateOrReadRecord || canOnlySeeDataGrid) {
      const newValue = value ?? [];
      if (newValue.length > 0 && newValue[0].id) {
        linkedRecordIdsRef.current = newValue.map((item) => item.id.toString());
        setLinkedRecords(newValue);
      } else if (newValue.length === 0) {
        linkedRecordIdsRef.current = [];
        setLinkedRecords(newValue);
      }
    }
  }, [value, canCreateOrReadRecord, canOnlySeeDataGrid]);

  useEffect(() => {
    if (linkedRecordsData && linkedRecords) {
      const fieldReverseLinkedRecordIds = linkedRecordsData
        .filter((record) => record.canRead)
        .map((record) => String(record.id));
      const recordReverseLinkedRecordsIds = linkedRecords.map((record) => String(record.id));
      const matchingIds = recordReverseLinkedRecordsIds.filter((id) => fieldReverseLinkedRecordIds.includes(id));
      const finalIdList = matchingIds.filter((id) => !listOfPermittedRecordIds.includes(id)); // make sure the id isn't already in the list
      setListOfPermittedRecordIds((prev) => [...prev, ...finalIdList]);
      setPendingPermissions([]);
    }
  }, [linkedRecordsData]);

  const isRowClickable = useMemo(() => {
    return !isPreview && linkedRecordsPermissions?.read;
  }, [isPreview, linkedRecordsPermissions]);

  const gridColumns = useMemo(() => {
    return recordLinkFormat
      ? transformRecordLinkColumns(
          recordLinkFormat,
          linkedRecordColumnValuePaths,
          getColumnSortPath,
          linkedRecordsCellRenderer
        )
      : [];
  }, [recordLinkFormat]);

  const gridColumnsWithActions = [
    ...gridColumns,
    {
      id: 0,
      field: '',
      title: '',
      visible: !readOnly,
      cellRenderer: 'LINKED_RECORDS_ACTIONS',
      type: DataGridColumnType.Custom,
      disableSort: true,
    },
  ];

  const moveOptionUp = useCallback((event: React.MouseEvent<HTMLButtonElement>, rowIndex: number) => {
    event.stopPropagation();

    setLinkedRecords((prevRecords) => {
      const updatedRecordsOrder = [...prevRecords];
      [updatedRecordsOrder[rowIndex - 1], updatedRecordsOrder[rowIndex]] = [
        updatedRecordsOrder[rowIndex],
        updatedRecordsOrder[rowIndex - 1],
      ];
      return updatedRecordsOrder;
    });
  }, []);

  const moveOptionDown = useCallback((event: React.MouseEvent<HTMLButtonElement>, rowIndex: number) => {
    event.stopPropagation();

    setLinkedRecords((prevRecords) => {
      const updatedRecordsOrder = [...prevRecords];
      [updatedRecordsOrder[rowIndex], updatedRecordsOrder[rowIndex + 1]] = [
        updatedRecordsOrder[rowIndex + 1],
        updatedRecordsOrder[rowIndex],
      ];
      return updatedRecordsOrder;
    });
  }, []);

  const gridSettings: DataGridSettings = {
    ...defaultLinkedRecordGridSetting,
    rowIsClickable: isRowClickable,
    columns: gridColumnsWithActions,
    autoSizeColumns: true,
    allowCellScrollBar: true,
    sortFunction: (data: JsonData, sorts: Array<ContinuousScrollerSort>) =>
      linkedRecordsSortFunction(data, sorts, gridColumnsWithActions),
    components: {
      ...defaultLinkedRecordGridSetting.components,
      ...customLinkedRecordsComponents,
      LINKED_RECORDS_ACTIONS: {
        CellComponent: (props: DataGridCellRendererProps) => {
          return (
            <LinkedRecordsActionsCell
              rowData={props.rowData}
              removeLinkedRecord={handleRemoveLinkedRecord}
              moveOptionDown={moveOptionDown}
              moveOptionUp={moveOptionUp}
              numberOfRows={linkedRecords.length}
              isTextualQuestionnaireLinkedRecords={isTextualQuestionnaireLinkedRecords}
            />
          );
        },
      },
      HISTORY: {
        CellComponent: (props: DataGridCellRendererProps, ref: Ref<HTMLDivElement>) => (
          <HistoryFieldDataCell {...props} ref={ref} allowCellScrollBar />
        ),
      },
    },
  };

  const rowTemplate: RowTemplateType = OdinDataGridRowTemplate({
    modules: modules,
    activeRecordReference: activeRecordReference,
    isClickable: isRowClickable,
    listOfPermittedRecordIds: listOfPermittedRecordIds,
    pendingLinkedRecords: pendingPermissions,
    openerRecordId: recordId ? String(recordId) : undefined,
  });

  const onSelectLinkedRecordsDialogOpen = () => setSelectLinkedRecordsDialogVisible(true);
  const onSelectLinkedRecordsDialogHidden = () => setSelectLinkedRecordsDialogVisible(false);

  const onCreateLinkedRecordDialogOpen = () => {
    if (recordId) {
      //Using cached data
      getRecordById({ id: recordId, keyTypeId: true }, true)
        .unwrap()
        .then((data) => {
          // if it's an existing record, there is no need to save before opening the create linked record dialog
          setCreateLinkedRecordDialogState({ visible: true, data: data.original });
        });
    } else {
      const resultObservable = saveRecord?.();
      if (resultObservable) {
        resultObservable
          .pipe(
            skipWhile((result) => isEmptyObject(forceAssert<Record<string, unknown>>(result))),
            take(1),
            takeUntil(destroySubject.current)
          )
          .subscribe((recordResult: RecordResult) => {
            setCreateLinkedRecordDialogState({ visible: true, data: recordResult });
          });
      }
    }
  };

  const onSaveLayout = () => {
    if (recordId && id) {
      const rawData = gridRef.current?.api?.data?.staticData ?? [];
      const currentSorts = gridRef.current?.api?.sortedFields?.getSortedFields() ?? [];

      // Apply the current sort to ensure the saved order matches what the user sees in the UI
      const sortedData =
        currentSorts.length > 0 ? linkedRecordsSortFunction(rawData, currentSorts, gridColumnsWithActions) : rawData;

      const sortedRecordIds = sortedData.map((record) => record.id as number);
      promiseToast(
        saveLayout({
          recordId,
          linkedRecordsFieldId: id as number,
          linkedRecordIds: sortedRecordIds,
        }).unwrap(),
        {
          success: t('save-order-success'),
          error: t('save-order-failed'),
          loading: t('performing-save'),
        }
      );
    }
  };

  const onCreateLinkedRecordDialogHidden = () => setCreateLinkedRecordDialogState({ visible: false, data: undefined });

  const handleAddLinkedRecords = useCallback(
    (updatedLinkedRecordIds: Array<string>) => {
      onChange?.(updatedLinkedRecordIds);
      saveRecord?.(); // save the Record when a linked record is selected/de-selected (MYOSH-1557)
    },
    [onChange, saveRecord]
  );

  const handleRecordCreated = useCallback(
    (addedRecordId: number) => {
      handleAddLinkedRecords([...(linkedRecordIdsRef.current ?? []), addedRecordId.toString()]);
    },
    [handleAddLinkedRecords]
  );

  const handleRemoveLinkedRecord = useCallback(
    (rowId: string) => {
      const filteredList: string[] = linkedRecordIdsRef.current?.filter((id) => id !== rowId) ?? [];
      onChange?.(filteredList);
      saveRecord?.(); // save the Record when a linked record is removed (MYOSH-5074)
    },
    [onChange, saveRecord]
  );

  const transformedData = !isTextualQuestionnaireLinkedRecords
    ? forceAssert<JsonData>(linkedRecords)
    : transformLinkedRecordDataWithKeyTypeId(forceAssert<JsonData>(linkedRecords));

  const hasSelectButton = showSelectButton && linkedRecordsPermissions?.read && !autoPopulateEnabled;
  const hasCreateButton =
    recordLinkRecordCreationDisabled !== true && linkedRecordsPermissions?.create && !autoPopulateEnabled;

  const buttonsContainerStyles = clsx('my-2 flex justify-between mx-1 gap-2', {
    'border border-error': error && !readOnly,
  });

  const rowIndicator = useMemo(() => {
    return {
      indicator: (data?: JsonDataItem, className?: string) => {
        const backgroundColor = linkedWorkflowSteps?.find(
          (step: ExtendedDynamicFormWorkflowStep) => step.label === data?.status
        )?.backgroundColor;
        const appliedStyles = className || 'h-4 w-4 rounded';
        return <div className={appliedStyles} style={{ backgroundColor: backgroundColor }} />;
      },
    };
  }, [linkedWorkflowSteps]);

  const onDataGridRefCreated = (dataGridRef: DataGridRef) => {
    gridRef.current = dataGridRef;
  };

  const canShowGrid = !areModulesLoading && (canCreateOrReadRecord || canOnlySeeDataGrid) && linkedRecords.length > 0;

  const { isMobile } = useScreenSize();
  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      {!readOnly && !isPreview && (
        <div className={buttonsContainerStyles}>
          <div className="flex gap-2">
            {hasCreateButton && (
              <Button type="primary" variant="alternative" onClick={onCreateLinkedRecordDialogOpen}>
                {buttonCaption || t('create')}
              </Button>
            )}
            {hasSelectButton && (
              <Button variant="alternative" classNames="!bg-mono-1" onClick={onSelectLinkedRecordsDialogOpen}>
                {t('select')}
              </Button>
            )}
          </div>
          {linkedRecords.length > 0 && !isTextualQuestionnaireLinkedRecords && !isMobile && (
            <div
              className="flex cursor-pointer items-center justify-center bg-transparent font-medium leading-5 text-primary-1"
              onClick={onSaveLayout}
            >
              {isLoading ? (
                <div className="h-5 w-5 animate-spin rounded-full border-b-2 border-t-2 border-primary-1"></div>
              ) : (
                t('save-order')
              )}
            </div>
          )}
        </div>
      )}
      {readOnly && (!linkedRecords || linkedRecords.length === 0) && <EmptyFieldLabel />}
      {!isMobile && canShowGrid && (
        <div className="my-2 flex max-h-[40rem]">
          <DataGrid
            ref={(gridRef) => {
              if (gridRef) onDataGridRefCreated(gridRef);
            }}
            data={transformedData}
            gridSettings={gridSettings}
            rowTemplate={rowTemplate}
            showSettings={false}
            rowIndicator={rowIndicator}
          />
        </div>
      )}
      {isMobile && canShowGrid && (
        <MobileLinkedBaseRecords
          isRowClickable={isRowClickable}
          data={transformedData}
          readOnly={readOnly}
          gridColumns={gridColumns}
          listOfPermittedRecordIds={listOfPermittedRecordIds}
          pendingPermissions={pendingPermissions}
          isLinkedRecordsFetching={isLinkedRecordsFetching}
          removeLinkedRecord={handleRemoveLinkedRecord}
          modules={modules}
          rowIndicator={rowIndicator}
        />
      )}
      {selectLinkedRecordsDialogVisible && (
        <LinkedRecordsSelecting
          recordId={recordId}
          visible={selectLinkedRecordsDialogVisible}
          hidden={onSelectLinkedRecordsDialogHidden}
          applyCallback={handleAddLinkedRecords}
          sourceModuleFormId={sourceModuleFormId}
          targetModuleFormId={targetModuleFormId}
          recordLinkType={recordLinkType}
          value={linkedRecordIdsRef.current ?? []}
          linkedWorkflowSteps={linkedWorkflowSteps}
          isMobile={isMobile}
        />
      )}
      {targetModuleFormId && (
        <CreateLinkedRecord
          header={t('new-sufix', { sufix: formPermissions?.formCaption })}
          visible={createLinkedRecordDialogState.visible}
          data={createLinkedRecordDialogState.data}
          formId={targetModuleFormId}
          hidden={onCreateLinkedRecordDialogHidden}
          onRecordCreated={handleRecordCreated}
          fieldId={id as number}
          parentRecordId={recordId}
          isTextualQuestionnaireLinkedRecords={isTextualQuestionnaireLinkedRecords}
        />
      )}
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}
